import { useState, useCallback, useRef } from 'react'

// ─── Answer Evaluation Types (mirrors Unity AnswerEvaluationType) ─
export const AnswerEvaluationType = {
    NotDefined: 0,
    ScorableAndCorrect: 1,
    ScorableAndIncorrect: 2,
    ScorableAndFailure: 3,
    NotScorable: 4,
}

// ─── Interactive Element Types ───────────────────────────────
const InteractiveElementType = {
    MultiChoice: 1,
    FillInBlankWriting: 2,
    FillInBlankDistractors: 3,
    Definition: 10,
    AssociationPuzzle: 11,
    AssociationPuzzleSequential: 12,
    AssociationPuzzleSequentialKeepRight: 16,
    WritingAiEvaluationInput: 17,
    WritingTaskEvaluationInput: 20,
}

// ─── Constants matching Unity LessonScoringLogic.cs ──────────
const XP_SCORABLE_PERFECT = 10
const XP_SCORABLE_CORRECT = 5
const XP_SCORABLE_INCORRECT = 2
const XP_SCORABLE_FAILURE = 2
const XP_NOT_SCORABLE = 1

const MAX_FILL_IN_BLANK_WRITING_TRIES = 2
const WRITING_AI_INTERACTION_WEIGHT = 6

const DEFAULT_ONE_STAR = 0.2
const DEFAULT_TWO_STAR = 0.65
const DEFAULT_THREE_STAR = 0.95

// ─── Star calculation (matches Unity EvaluateLesson) ─────────
function calculateStars(normalizedScore, hasFailed = false, thresholds = null) {
    if (hasFailed) return 0

    const oneStar = thresholds?.[0] ?? DEFAULT_ONE_STAR
    const twoStar = thresholds?.[1] ?? DEFAULT_TWO_STAR
    const threeStar = thresholds?.[2] ?? DEFAULT_THREE_STAR

    if (normalizedScore >= threeStar) return 3
    if (normalizedScore >= twoStar) return 2
    if (normalizedScore >= oneStar) return 1
    return 0
}

// ─── Evaluate Speak / BranchingMultiChoice (matches Unity EvaluateBranchingStage) ─
function evaluateConversationalStage(node, nodeResult) {
    const chosenIndex = nodeResult.chosenAlternativeIndex
    if (chosenIndex < 0 || chosenIndex >= (node.options?.length || 0)) {
        return {
            scenarioNodeId: node.nodeId,
            scenarioNodeType: node.scenarioNodeType,
            answerEvaluationType: AnswerEvaluationType.NotScorable,
            score: 0,
            maxScore: 0,
            xp: XP_NOT_SCORABLE,
            answersSubmitted: 0,
            correctAnswersSubmitted: 0,
            interactiveResults: [],
            nodeResultDto: nodeResult,
        }
    }

    if (nodeResult.skipped) {
        const skipMaxScore = node.lessonNodeScoringConfigV1Dto?.normalizedMaxScore ?? 1
        return {
            scenarioNodeId: node.nodeId,
            scenarioNodeType: node.scenarioNodeType,
            answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
            score: 0,
            maxScore: skipMaxScore,
            xp: 0,
            answersSubmitted: 0,
            correctAnswersSubmitted: 0,
            interactiveResults: [],
            nodeResultDto: nodeResult,
        }
    }

    const option = node.options[chosenIndex]
    const scoringConfig = node.lessonNodeScoringConfigV1Dto

    let normalizedMaxScore = 1
    if (scoringConfig != null) {
        normalizedMaxScore = scoringConfig.normalizedMaxScore ?? 1
    }

    // Per-option custom score from config
    let customOptionScore = null
    if (scoringConfig?.lessonNodeOptionNormalizedScoreV1Dtos) {
        const optionScoring = scoringConfig.lessonNodeOptionNormalizedScoreV1Dtos.find(
            (x) => x.optionIndex === chosenIndex
        )
        if (optionScoring) {
            customOptionScore = optionScoring.normalizedScore
        }
    }

    // Failure option
    if (option.failure) {
        return {
            scenarioNodeId: node.nodeId,
            scenarioNodeType: node.scenarioNodeType,
            answerEvaluationType: AnswerEvaluationType.ScorableAndFailure,
            score: 0,
            maxScore: normalizedMaxScore,
            xp: XP_SCORABLE_FAILURE,
            answersSubmitted: 1,
            correctAnswersSubmitted: 0,
            interactiveResults: [],
            nodeResultDto: nodeResult,
        }
    }

    // Negative score alteration → incorrect
    if (option.scoreAlteration < 0) {
        return {
            scenarioNodeId: node.nodeId,
            scenarioNodeType: node.scenarioNodeType,
            answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
            score: 0,
            maxScore: normalizedMaxScore,
            xp: XP_SCORABLE_INCORRECT,
            answersSubmitted: 1,
            correctAnswersSubmitted: 0,
            interactiveResults: [],
            nodeResultDto: nodeResult,
        }
    }

    // Correct answer
    let score = 1
    if (customOptionScore != null) {
        score = customOptionScore
    } else {
        const allOptionsCorrect = node.options.every(
            (opt) => (opt.scoreAlteration === 0 || opt.scoreAlteration == null) && !opt.failure
        )
        if (allOptionsCorrect) {
            score = 0.1
            normalizedMaxScore = 0.1
        }
    }

    return {
        scenarioNodeId: node.nodeId,
        scenarioNodeType: node.scenarioNodeType,
        answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
        score,
        maxScore: normalizedMaxScore,
        xp: XP_SCORABLE_PERFECT,
        answersSubmitted: 1,
        correctAnswersSubmitted: 1,
        interactiveResults: [],
        nodeResultDto: nodeResult,
    }
}

// ─── Evaluate non-scored stage ───────────────────────────────
function evaluateNonScoredStage(node, nodeResult) {
    // Brief (1) and Debrief (6) get 0 XP; other non-scored get XP_NOT_SCORABLE
    const isBriefOrDebrief = node.scenarioNodeType === 1 || node.scenarioNodeType === 6
    return {
        scenarioNodeId: node.nodeId,
        scenarioNodeType: node.scenarioNodeType,
        answerEvaluationType: AnswerEvaluationType.NotScorable,
        score: 0,
        maxScore: 0,
        xp: isBriefOrDebrief ? 0 : XP_NOT_SCORABLE,
        answersSubmitted: 0,
        correctAnswersSubmitted: 0,
        interactiveResults: [],
        nodeResultDto: nodeResult,
    }
}

// ─── Not-scorable interactive result helper ──────────────────
function notScorableResult(testOption) {
    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.NotScorable,
        score: 0,
        maxScore: 0,
        xp: XP_NOT_SCORABLE,
        answersSubmitted: 0,
        correctAnswersSubmitted: 0,
    }
}

// ═══════════════════════════════════════════════════════════════
//  Per-type interactive evaluators (match Unity LessonScoringLogic)
// ═══════════════════════════════════════════════════════════════

// ─── MultiChoice (type 1) — EvaluateMultichoiceInteractive ──
function evaluateMultichoiceInteractive(testOption, answers) {
    if (!answers || answers.length === 0) {
        return notScorableResult(testOption)
    }

    const firstAnswer = answers[0]
    if (firstAnswer?.correct) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
            score: 1,
            maxScore: 1,
            xp: XP_SCORABLE_PERFECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: 1,
        }
    }

    const correctCount = answers.filter((a) => a.correct).length
    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
        score: 0,
        maxScore: 1,
        xp: XP_SCORABLE_INCORRECT,
        answersSubmitted: answers.length,
        correctAnswersSubmitted: correctCount,
    }
}

// ─── FillInBlankWriting (type 2) — EvaluateFillInBlankWritingInteractive ──
function evaluateFillInBlankWritingInteractive(testOption, answers, shouldAllowForErrors) {
    if (!answers || answers.length === 0) {
        return notScorableResult(testOption)
    }

    let score = 1
    let correctAnswers = 0

    if (shouldAllowForErrors) {
        // Exam mode: any wrong answer → score 0
        for (const answer of answers) {
            if (!answer.correct) {
                score = 0
                break
            }
        }
        correctAnswers = answers.filter((a) => a.correct).length
        const xp = score >= 0.99 ? XP_SCORABLE_PERFECT : XP_SCORABLE_INCORRECT
        const evalType = score > 0
            ? AnswerEvaluationType.ScorableAndCorrect
            : AnswerEvaluationType.ScorableAndIncorrect
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: evalType,
            score,
            maxScore: 1,
            xp,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: correctAnswers,
        }
    }

    // Normal mode: -0.1 per failed attempt, max MAX_FILL_IN_BLANK_WRITING_TRIES
    // Each answer may carry failCount metadata from the interaction.
    for (const answer of answers) {
        const failCount = answer.failCount ?? 0
        if (failCount === 0 && answer.correct) {
            correctAnswers++
        } else if (failCount >= MAX_FILL_IN_BLANK_WRITING_TRIES) {
            score = 0
            break
        } else if (answer.correct) {
            score -= 0.1 * failCount
            correctAnswers++
        } else {
            score = 0
            break
        }
    }

    if (score < 0.0001) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
            score: 0,
            maxScore: 1,
            xp: XP_SCORABLE_INCORRECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: correctAnswers,
        }
    }

    const xp = score >= 1 ? XP_SCORABLE_PERFECT : XP_SCORABLE_CORRECT
    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
        score,
        maxScore: 1,
        xp,
        answersSubmitted: answers.length,
        correctAnswersSubmitted: correctAnswers,
    }
}

// ─── FillInBlankDistractors (type 3) — EvaluateFillInBlankDistractorInteractive ──
function evaluateFillInBlankDistractorInteractive(testOption, answers) {
    if (!answers || answers.length === 0) {
        return notScorableResult(testOption)
    }

    const alternatives = testOption.alternatives || []

    // Single correct alternative → small fixed score (0.1 or config)
    if (alternatives.length === 1 && alternatives[0].correct && answers[0]?.correct) {
        const normalizedMaxScore = testOption.interactionScoringConfigV1Dto?.normalizedMaxScore ?? 0.1
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
            score: normalizedMaxScore,
            maxScore: normalizedMaxScore,
            xp: XP_SCORABLE_CORRECT,
            answersSubmitted: 1,
            correctAnswersSubmitted: 1,
        }
    }

    // Detect text builder: all alternatives correct with distinct startIndices
    let isTextBuilder = alternatives.length > 0
    for (let i = 0; i < alternatives.length && isTextBuilder; i++) {
        if (!alternatives[i].correct) {
            isTextBuilder = false
            break
        }
        for (let j = i + 1; j < alternatives.length && isTextBuilder; j++) {
            if (alternatives[i].startIndex === alternatives[j].startIndex) {
                isTextBuilder = false
            }
        }
    }

    if (isTextBuilder) {
        return evaluateTextBuilderInteractive(testOption, answers)
    }

    return evaluateDistractorMultichoiceInteractive(testOption, answers)
}

// ─── Text builder sub-evaluator — EvaluateDistractorTextBuilderInteractive ──
function evaluateTextBuilderInteractive(testOption, answers) {
    const alternatives = testOption.alternatives || []
    if (!answers || answers.length === 0 || alternatives.length === 0) {
        return notScorableResult(testOption)
    }

    // Sum non-repeated wrong tries from answer metadata
    const nonRepeatedWrongTries = answers.reduce((sum, a) => sum + (a.wrongTryCount || 0), 0)

    const distractorCount = alternatives.length
    let interactiveScore = 1
    if (distractorCount - 1 > 0) {
        interactiveScore -= (1 / (distractorCount - 1)) * nonRepeatedWrongTries
        interactiveScore = Math.max(0, interactiveScore)
    }

    const correctCount = answers.filter((a) => a.correct).length

    if (interactiveScore < 0.0001) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
            score: 0,
            maxScore: 1,
            xp: XP_SCORABLE_INCORRECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: correctCount,
        }
    }

    const xp = interactiveScore >= 1 ? XP_SCORABLE_PERFECT : XP_SCORABLE_CORRECT
    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
        score: interactiveScore,
        maxScore: 1,
        xp,
        answersSubmitted: answers.length,
        correctAnswersSubmitted: correctCount,
    }
}

// ─── Distractor multichoice sub-evaluator — EvaluateDistractorMultichoiceInteractive ──
function evaluateDistractorMultichoiceInteractive(testOption, answers) {
    const firstAnswer = answers[0]
    if (firstAnswer?.correct) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
            score: 1,
            maxScore: 1,
            xp: XP_SCORABLE_PERFECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: 1,
        }
    }

    const correctCount = answers.filter((a) => a.correct).length
    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
        score: 0,
        maxScore: 1,
        xp: XP_SCORABLE_INCORRECT,
        answersSubmitted: answers.length,
        correctAnswersSubmitted: correctCount,
    }
}

// ─── Definition (type 10) — EvaluateDefinitionStage ──────────
function evaluateDefinitionInteractive(testOption, answers) {
    if (!answers || answers.length === 0) {
        return notScorableResult(testOption)
    }

    const correctPronunciations = answers.filter((a) => a.correct).length
    const firstCorrect = answers.length > 0 && answers[0].correct

    if (firstCorrect) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
            score: 1,
            maxScore: 1,
            xp: XP_SCORABLE_PERFECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: correctPronunciations,
        }
    }

    if (correctPronunciations > 0) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
            score: 1,
            maxScore: 1,
            xp: XP_SCORABLE_CORRECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: correctPronunciations,
        }
    }

    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
        score: 0,
        maxScore: 1,
        xp: XP_SCORABLE_INCORRECT,
        answersSubmitted: answers.length,
        correctAnswersSubmitted: 0,
    }
}

// ─── AssociationPuzzle (type 11) — EvaluateAssociationPuzzleInteractive ──
function evaluateAssociationPuzzleInteractive(testOption, answers) {
    const alternatives = testOption.alternatives || []
    if (!answers || answers.length === 0 || alternatives.length === 0) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.NotScorable,
            score: 0,
            maxScore: 0,
            xp: XP_NOT_SCORABLE,
            answersSubmitted: answers?.length || 0,
            correctAnswersSubmitted: 0,
        }
    }

    let nonRepeatedWrongTries = 0
    let prevAnswer = ''
    for (const answer of answers) {
        if (prevAnswer === answer.answer && !answer.correct) {
            continue
        }
        if (!answer.correct) {
            nonRepeatedWrongTries++
        }
        prevAnswer = answer.answer
    }

    const pairCount = alternatives.length
    let interactiveScore = 1
    if (pairCount - 1 > 0) {
        interactiveScore -= (1 / (pairCount - 1)) * nonRepeatedWrongTries
        interactiveScore = Math.max(0, interactiveScore)
    }

    const correctCount = answers.filter((a) => a.correct).length

    if (interactiveScore < 0.0001) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
            score: 0,
            maxScore: 1,
            xp: XP_SCORABLE_INCORRECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: correctCount,
        }
    }

    const xp = interactiveScore >= 1 ? XP_SCORABLE_PERFECT : XP_SCORABLE_CORRECT
    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
        score: interactiveScore,
        maxScore: 1,
        xp,
        answersSubmitted: answers.length,
        correctAnswersSubmitted: correctCount,
    }
}

// ─── AssociationPuzzleSequential (types 12, 16) — EvaluateAssociationPuzzleSequentialInteractive ──
function evaluateAssociationPuzzleSequentialInteractive(testOption, answers) {
    const alternatives = testOption.alternatives || []
    if (!answers || answers.length === 0 || alternatives.length === 0) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.NotScorable,
            score: 0,
            maxScore: 0,
            xp: XP_NOT_SCORABLE,
            answersSubmitted: answers?.length || 0,
            correctAnswersSubmitted: 0,
        }
    }

    let associationScore = 0
    let prevAnswer = ''
    let didMistakeOnSingle = false

    for (const answer of answers) {
        if (prevAnswer === answer.answer && !answer.correct) {
            continue
        }
        if (answer.correct) {
            if (!didMistakeOnSingle) {
                associationScore++
            }
            didMistakeOnSingle = false
        } else {
            didMistakeOnSingle = true
        }
        prevAnswer = answer.answer
    }

    const pairCount = alternatives.length
    let interactiveScore = 0
    if (associationScore === pairCount) {
        interactiveScore = 1
    } else if (pairCount > 0) {
        interactiveScore = Math.max(0, (associationScore - 1) / pairCount)
    }

    const correctCount = answers.filter((a) => a.correct).length

    if (interactiveScore < 0.0001) {
        return {
            testOptionId: testOption.testOptionId,
            answerEvaluationType: AnswerEvaluationType.ScorableAndIncorrect,
            score: 0,
            maxScore: 1,
            xp: XP_SCORABLE_INCORRECT,
            answersSubmitted: answers.length,
            correctAnswersSubmitted: correctCount,
        }
    }

    const xp = interactiveScore >= 1 ? XP_SCORABLE_PERFECT : XP_SCORABLE_CORRECT
    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: AnswerEvaluationType.ScorableAndCorrect,
        score: interactiveScore,
        maxScore: 1,
        xp,
        answersSubmitted: answers.length,
        correctAnswersSubmitted: correctCount,
    }
}

// ─── WritingAiEvaluationInput (type 17) — server-side AI evaluation ──
function evaluateWritingAiInteractive(testOption, answers) {
    if (!answers || answers.length === 0) {
        return notScorableResult(testOption)
    }

    const firstAnswer = answers[0]
    const normalizedScore = firstAnswer.normalizedScore ?? (firstAnswer.correct ? 1 : 0)
    const isCorrect = normalizedScore >= 0.5

    return {
        testOptionId: testOption.testOptionId,
        answerEvaluationType: isCorrect
            ? AnswerEvaluationType.ScorableAndCorrect
            : AnswerEvaluationType.ScorableAndIncorrect,
        score: WRITING_AI_INTERACTION_WEIGHT * normalizedScore,
        maxScore: WRITING_AI_INTERACTION_WEIGHT,
        xp: isCorrect ? XP_SCORABLE_CORRECT : XP_SCORABLE_INCORRECT,
        answersSubmitted: 1,
        correctAnswersSubmitted: isCorrect ? 1 : 0,
    }
}

// ═══════════════════════════════════════════════════════════════
//  Main interactive evaluator dispatch (matches Unity EvaluateInteractive)
// ═══════════════════════════════════════════════════════════════

export function evaluateInteractive(testOption, answers, shouldAllowForErrors = false) {
    const elementType =
        testOption.scenarioInteractiveElementType ?? testOption.interactiveElementType

    // WritingAi interactions are always scored — the AI API always returns a normalizedScore,
    // so bypassing the shouldBeScored check prevents stale/incorrect configs from producing 0/0.
    if (elementType === InteractiveElementType.WritingAiEvaluationInput ||
        elementType === InteractiveElementType.WritingTaskEvaluationInput) {
        return evaluateWritingAiInteractive(testOption, answers)
    }

    const interactiveShouldBeScored =
        testOption.interactionScoringConfigV1Dto?.shouldBeScored !== false

    if (!interactiveShouldBeScored) {
        return notScorableResult(testOption)
    }

    switch (elementType) {
        case InteractiveElementType.MultiChoice:
            return evaluateMultichoiceInteractive(testOption, answers)

        case InteractiveElementType.FillInBlankWriting:
            return evaluateFillInBlankWritingInteractive(testOption, answers, shouldAllowForErrors)

        case InteractiveElementType.FillInBlankDistractors:
            return evaluateFillInBlankDistractorInteractive(testOption, answers)

        case InteractiveElementType.Definition:
            return evaluateDefinitionInteractive(testOption, answers)

        case InteractiveElementType.AssociationPuzzle:
            return evaluateAssociationPuzzleInteractive(testOption, answers)

        case InteractiveElementType.AssociationPuzzleSequential:
        case InteractiveElementType.AssociationPuzzleSequentialKeepRight:
            return evaluateAssociationPuzzleSequentialInteractive(testOption, answers)

        default:
            return notScorableResult(testOption)
    }
}

// ─── Evaluate a test stage (types 7-14, 15, 16) ──────────────
function evaluateTestStage(node, nodeResult, interactiveResults) {
    if (!interactiveResults || interactiveResults.length === 0) {
        return evaluateNonScoredStage(node, nodeResult)
    }

    let totalScore = 0
    let totalMaxScore = 0
    let totalXp = 0
    let totalAnswers = 0
    let totalCorrect = 0

    interactiveResults.forEach((ir) => {
        totalScore += ir.score
        totalMaxScore += ir.maxScore
        totalXp += ir.xp
        totalAnswers += ir.answersSubmitted
        totalCorrect += ir.correctAnswersSubmitted
    })

    let evalType = AnswerEvaluationType.ScorableAndCorrect
    if (totalCorrect === 0 && totalAnswers > 0) {
        evalType = AnswerEvaluationType.ScorableAndIncorrect
    } else if (totalCorrect < totalAnswers) {
        evalType = AnswerEvaluationType.ScorableAndIncorrect
    }

    return {
        scenarioNodeId: node.nodeId,
        scenarioNodeType: node.scenarioNodeType,
        answerEvaluationType: evalType,
        score: totalScore,
        maxScore: totalMaxScore,
        xp: totalXp,
        answersSubmitted: totalAnswers,
        correctAnswersSubmitted: totalCorrect,
        interactiveResults,
        nodeResultDto: nodeResult,
    }
}

// ═══════════════════════════════════════════════════════════════
//  Hook
// ═══════════════════════════════════════════════════════════════

export default function useLessonScoring() {
    const [stageResults, setStageResults] = useState([])
    const orderedStageResults = useRef([])

    const lessonScore = stageResults.reduce((sum, r) => sum + r.score, 0)
    const lessonMaxScore = stageResults.reduce((sum, r) => sum + r.maxScore, 0)
    const lessonXp = stageResults.reduce((sum, r) => sum + r.xp, 0)
    const answersSubmitted = stageResults.reduce((sum, r) => sum + r.answersSubmitted, 0)
    const correctAnswersSubmitted = stageResults.reduce((sum, r) => sum + r.correctAnswersSubmitted, 0)

    // If lessonMaxScore is 0 but test-type nodes were played, a scoring config is broken — use 0.
    // Only default to 1 (pass) for purely informational lessons with no test nodes at all.
    let normalizedScore
    if (lessonMaxScore > 0) {
        normalizedScore = lessonScore / lessonMaxScore
    } else {
        const hasTestNodes = stageResults.some((r) => {
            const t = r.scenarioNodeType
            return (t >= 7 && t <= 14) || t === 15 || t === 16 || t === 19
        })
        if (hasTestNodes) {
            console.warn('[useLessonScoring] lessonMaxScore is 0 but test nodes exist — possible scoring config error')
            normalizedScore = 0
        } else {
            normalizedScore = 1
        }
    }
    const hasFailed = stageResults.some(
        (r) => r.answerEvaluationType === AnswerEvaluationType.ScorableAndFailure
    )
    const stars = calculateStars(normalizedScore, hasFailed)

    const addStageReport = useCallback((node, nodeResult, interactiveResults = null) => {
        let report
        const nodeType = node.scenarioNodeType

        // Conversational nodes (Speak=3, BranchingMultiChoice=5)
        if (nodeType === 3 || nodeType === 5) {
            // Check ShouldBeScored config (matches Unity EvaluateStage)
            const config = node.lessonNodeScoringConfigV1Dto
            if (config == null || config.shouldBeScored !== false) {
                report = evaluateConversationalStage(node, nodeResult)
            } else {
                report = evaluateNonScoredStage(node, nodeResult)
            }
        }
        // Test nodes (7-14, 15, 16, 19=WritingAiEvaluation)
        else if ((nodeType >= 7 && nodeType <= 14) || nodeType === 15 || nodeType === 16 || nodeType === 19) {
            report = evaluateTestStage(node, nodeResult, interactiveResults)
        }
        // Non-scored nodes (Watch, Listen, Instruction, Brief, Debrief)
        else {
            report = evaluateNonScoredStage(node, nodeResult)
        }

        orderedStageResults.current.push(report)
        setStageResults((prev) => {
            const existingIdx = prev.findIndex((r) => r.scenarioNodeId === report.scenarioNodeId)
            if (existingIdx >= 0) {
                if (report.score >= prev[existingIdx].score) {
                    const updated = [...prev]
                    updated[existingIdx] = report
                    return updated
                }
                return prev
            }
            return [...prev, report]
        })

        return report
    }, [])

    const reset = useCallback(() => {
        setStageResults([])
        orderedStageResults.current = []
    }, [])

    return {
        stageResults,
        orderedStageResults: orderedStageResults.current,
        lessonScore,
        lessonMaxScore,
        lessonXp,
        normalizedScore,
        stars,
        hasFailed,
        answersSubmitted,
        correctAnswersSubmitted,
        addStageReport,
        reset,
        calculateStars,
        evaluateInteractive,
    }
}
